import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'Redirect',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: t('router.login'),
      noTagsView: true
    }
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/dashboard',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Dashboard',
    meta: {
      title: t('router.dashboard'),
      icon: 'ant-design:dashboard-filled',
      alwaysShow: true
    },
    children: [
      {
        path: 'analysis',
        component: () => import('@/views/Dashboard/Analysis.vue'),
        name: 'Analysis',
        meta: {
          title: t('router.analysis'),
          noCache: true,
          affix: true
        }
      }
    ]
  },
  {
    path: '/customer-service',
    component: Layout,
    meta: {
      title: t('router.customerService'),
      icon: 'mdi:customer-service',
      alwaysShow: true
    },
    name: 'CustomerService',
    children: [
      {
        path: 'reply',
        component: () => import('@/views/CustomerService/Reply/Index.vue'),
        name: 'CustomerServiceReply',
        meta: {
          title: t('router.customerServiceReply'),
          noCache: true
        }
      }
    ]
  },
  {
    path: '/user',
    name: 'User',
    component: Layout,
    meta: {
      title: t('router.user'),
      icon: 'mdi:user',
      alwaysShow: true
    },
    children: [
      {
        path: 'history',
        name: 'History',
        component: () => import('@/views/User/History/index.vue'),
        meta: {
          title: t('router.historyChat')
        }
      },
      {
        path: 'chat-with-each-other',
        name: 'ChatEachOther',
        component: () => import('@/views/User/ChatEachOther/index.vue'),
        meta: {
          title: t('router.ChatEachOther')
        }
      },
      {
        path: 'picture-record',
        name: 'PictureRecord',
        component: () => import('@/views/User/PictureRecord/index.vue'),
        meta: {
          title: t('router.pictureRecord')
        }
      }
    ]
  },
  {
    path: '/game-settings',
    name: 'GameSettings',
    component: Layout,
    meta: {
      title: t('router.gameSettings'),
      icon: 'fluent-mdl2:game',
      alwaysShow: true
    },
    children: [
      {
        path: 'income',
        name: 'Income',
        component: () => import('@/views/GameSettings/Income/index.vue'),
        meta: {
          title: t('router.incomeReconciliation'),
          noCache: true
        }
      },
      {
        path: 'max-bonus',
        name: 'MaxBonus',
        component: () => import('@/views/GameSettings/MaxBonus/index.vue'),
        meta: {
          title: t('router.maxBonus'),
          noCache: true
        }
      },
      {
        path: 'seven',
        name: 'Seven',
        component: () => import('@/views/GameSettings/Seven/index.vue'),
        meta: {
          title: t('router.seven'),
          noCache: true
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  const resetWhiteNameList = ['Redirect', 'Login', 'NoFind', 'Root']
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !resetWhiteNameList.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
